/**
 * Page Editor Logger
 *
 * Shared debug logging utility for all page editor modules.
 * Uses the same gating pattern as lock_manager.js and presence_manager.js.
 *
 * Enable debug logging by:
 * - Setting window.PAGE_EDITOR_DEBUG = true
 * - Running on localhost
 * - Setting localStorage.pageEditorDebug = 'true'
 *
 * Phase 5 Code Quality - Task 3.0
 */

// Module-level debug gate - matches existing pattern from lock_manager.js:17-19
const DEBUG =
  typeof window !== 'undefined' &&
  (window.PAGE_EDITOR_DEBUG ||
    window.location?.hostname === 'localhost' ||
    localStorage?.getItem('pageEditorDebug') === 'true')

/**
 * Log a debug message (only when debug mode is enabled)
 * @param {string} prefix - Module prefix like '[SaveQueue]' or '[LockManager]'
 * @param {...any} args - Arguments to log
 */
export function log(prefix, ...args) {
  if (DEBUG) {
    console.log(prefix, ...args)
  }
}

/**
 * Log a warning (only when debug mode is enabled)
 * @param {string} prefix - Module prefix
 * @param {...any} args - Arguments to log
 */
export function warn(prefix, ...args) {
  if (DEBUG) {
    console.warn(prefix, ...args)
  }
}

/**
 * Log an error (always logged, even when debug mode is disabled)
 * @param {string} prefix - Module prefix
 * @param {...any} args - Arguments to log
 */
export function error(prefix, ...args) {
  // Errors are always logged regardless of debug mode
  console.error(prefix, ...args)
}

/**
 * Start a console group (only when debug mode is enabled)
 * @param {string} label - Group label
 */
export function group(label) {
  if (DEBUG) {
    console.group(label)
  }
}

/**
 * End a console group (only when debug mode is enabled)
 */
export function groupEnd() {
  if (DEBUG) {
    console.groupEnd()
  }
}

/**
 * Log a table (only when debug mode is enabled)
 * @param {any} data - Data to display in table format
 */
export function table(data) {
  if (DEBUG) {
    console.table(data)
  }
}

/**
 * Check if debug mode is enabled
 * @returns {boolean}
 */
export function isDebugEnabled() {
  return DEBUG
}

/**
 * Create a prefixed logger for a specific module
 * @param {string} moduleName - Module name (e.g., 'SaveQueue', 'LockManager')
 * @returns {Object} Logger with bound prefix
 *
 * @example
 * const logger = createLogger('SaveQueue')
 * logger.log('Processing item', item) // logs '[SaveQueue] Processing item ...'
 */
export function createLogger(moduleName) {
  const prefix = `[${moduleName}]`
  return {
    // Alias: some modules use logger.debug(...) even though the contract is log/warn/error.
    // Keep debug behavior gated the same as log.
    debug: (...args) => log(prefix, ...args),
    log: (...args) => log(prefix, ...args),
    warn: (...args) => warn(prefix, ...args),
    error: (...args) => error(prefix, ...args),
    group: (label) => group(`${prefix} ${label}`),
    groupEnd,
    table,
    isDebugEnabled
  }
}

// Default export for simple usage
export default {
  log,
  warn,
  error,
  group,
  groupEnd,
  table,
  isDebugEnabled,
  createLogger,
  DEBUG
}
